import {
  Activity,
  AlertTriangle,
  Building2,
  CheckCircle2,
  FileText,
  RefreshCw,
  ShieldAlert,
  Users,
} from 'lucide-react';
import { EmptyState } from '@components/common/EmptyState';
import { PageHeader } from '@components/common/PageHeader';
import { SectionCard } from '@components/common/SectionCard';
import { StatCard } from '@components/common/StatCard';
import { useFounderInsights } from '@hooks/useCRM';
import { formatDate, formatRelativeDate } from '@lib/format';
import type {
  FounderDimensionCount,
  FounderInsightKPI,
  FounderInsightSeverity,
  FounderIssueItem,
  FounderTenantHealth,
} from '@/types/api';

const kpiIcons: Record<string, JSX.Element> = {
  active_tenants: <Building2 size={18} />,
  active_users_30d: <Users size={18} />,
  documents_current_month: <FileText size={18} />,
  document_success_rate: <CheckCircle2 size={18} />,
  open_issues: <ShieldAlert size={18} />,
  pending_reviews: <Activity size={18} />,
};

const severityAccent: Record<FounderInsightSeverity, 'gold' | 'teal' | 'rose' | 'ink'> = {
  good: 'teal',
  neutral: 'ink',
  warning: 'gold',
  critical: 'rose',
};

function formatKpiValue(kpi: FounderInsightKPI) {
  if (typeof kpi.value === 'number') {
    const formatted = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: kpi.unit === '%' ? 1 : 0,
    }).format(kpi.value);
    return kpi.unit ? `${formatted}${kpi.unit}` : formatted;
  }
  return kpi.value;
}

function formatChange(value?: number | null) {
  if (value === null || value === undefined) {
    return null;
  }
  const prefix = value > 0 ? '+' : '';
  return `${prefix}${value.toFixed(1)}%`;
}

function statusLabel(value: string) {
  return value.replace(/_/g, ' ');
}

function ProgressRow({ metric }: { metric: FounderDimensionCount }) {
  const percent = metric.percent ?? 0;
  return (
    <div className="insight-progress-row">
      <div>
        <strong>{metric.label}</strong>
        <span>
          {metric.count}
          {metric.total ? ` of ${metric.total}` : ''}
        </span>
      </div>
      <div className="insight-progress-track" aria-label={`${metric.label} ${percent}%`}>
        <span style={{ width: `${Math.min(100, Math.max(0, percent))}%` }} />
      </div>
      <em>{metric.percent == null ? 'n/a' : `${metric.percent}%`}</em>
    </div>
  );
}

function TenantScore({ tenant }: { tenant: FounderTenantHealth }) {
  return (
    <div className="tenant-score">
      <span className={`tenant-score-dot tenant-score-${tenant.health_status}`} />
      <strong>{tenant.health_score}</strong>
      <span>{statusLabel(tenant.health_status)}</span>
    </div>
  );
}

function IssueItem({ issue }: { issue: FounderIssueItem }) {
  return (
    <article className={`stack-item founder-issue founder-issue-${issue.severity}`}>
      <div>
        <strong>{issue.title}</strong>
        <p>
          {issue.tenant_name || 'Unknown tenant'} • {issue.source.replace(/_/g, ' ')} • {formatRelativeDate(issue.occurred_at)}
        </p>
        {issue.detail ? <p>{issue.detail}</p> : null}
      </div>
      <span className={`pill pill-${issue.severity === 'critical' || issue.severity === 'error' ? 'low' : 'medium'}`}>
        {issue.severity}
      </span>
    </article>
  );
}

export function AppHealthPage() {
  const { data, isLoading, isError, isFetching, refetch } = useFounderInsights();

  return (
    <div className="space-y-8">
      <PageHeader
        title="App Health"
        subtitle={data?.generated_at ? `Updated ${formatRelativeDate(data.generated_at)}` : 'Founder view of product usage, customer health, and operational risk.'}
        action={(
          <button type="button" className="button button-secondary" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw size={16} />
            {isFetching ? 'Refreshing' : 'Refresh'}
          </button>
        )}
      />

      {isError ? (
        <SectionCard title="Unable to load app health">
          <EmptyState title="Insights are unavailable" body="The founder insights endpoint did not return data. Check the backend logs for the request correlation ID." />
        </SectionCard>
      ) : null}

      <section className="stats-grid">
        {data?.kpis.map((kpi) => (
          <StatCard
            key={kpi.key}
            label={kpi.label}
            value={formatKpiValue(kpi)}
            detail={[kpi.detail, formatChange(kpi.change_percent)].filter(Boolean).join(' • ')}
            accent={severityAccent[kpi.severity]}
            icon={kpiIcons[kpi.key] ?? <Activity size={18} />}
          />
        )) ?? Array.from({ length: 6 }).map((_, index) => (
          <StatCard
            key={`loading-${index}`}
            label="Loading"
            value="..."
            detail="Fetching founder insight metrics"
            accent="ink"
            icon={<Activity size={18} />}
          />
        ))}
      </section>

      <div className="dashboard-columns">
        <SectionCard title="Activation" subtitle="Core setup milestones across customer tenants.">
          {isLoading ? (
            <div className="loading-row">Loading activation…</div>
          ) : data?.activation.length ? (
            <div className="insight-progress-list">
              {data.activation.map((metric) => <ProgressRow key={metric.key} metric={metric} />)}
            </div>
          ) : (
            <EmptyState title="No customer tenants" body="Activation will appear after customers start onboarding." />
          )}
        </SectionCard>

        <SectionCard title="Document Mix" subtitle={`Last ${data?.window_days ?? 30} days by document type.`}>
          {isLoading ? (
            <div className="loading-row">Loading document mix…</div>
          ) : data?.usage_by_document_type.length ? (
            <div className="insight-progress-list">
              {data.usage_by_document_type.map((metric) => <ProgressRow key={metric.key} metric={metric} />)}
            </div>
          ) : (
            <EmptyState title="No documents yet" body="Document volume will appear once customers upload, email, or import AP documents." />
          )}
        </SectionCard>
      </div>

      <SectionCard title="Tenant Health" subtitle="Lowest scores first so the riskiest accounts stay visible.">
        {isLoading ? (
          <div className="loading-row">Loading tenant health…</div>
        ) : data?.tenant_health.length ? (
          <div className="table-wrap">
            <table className="crm-table founder-health-table">
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Health</th>
                  <th>Docs</th>
                  <th>Users</th>
                  <th>Issues</th>
                  <th>Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {data.tenant_health.map((tenant) => (
                  <tr key={tenant.tenant_id}>
                    <td>
                      <strong>{tenant.tenant_name}</strong>
                      <span>{tenant.plan_name || 'No plan'} • {tenant.subscription_status || tenant.status}</span>
                    </td>
                    <td><TenantScore tenant={tenant} /></td>
                    <td>
                      <strong>{tenant.docs_current_month}</strong>
                      <span>{tenant.docs_previous_month} previous month</span>
                    </td>
                    <td>{tenant.active_users_30d}</td>
                    <td>
                      <strong>{tenant.open_issues + tenant.stuck_documents + tenant.failed_notifications_7d}</strong>
                      <span>{tenant.pending_reviews} pending reviews</span>
                    </td>
                    <td>{tenant.last_activity_at ? formatDate(tenant.last_activity_at) : 'No activity'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="No tenant health rows" body="Customer tenants will appear here once they exist outside the internal EZ Flow workspace." />
        )}
      </SectionCard>

      <SectionCard
        title="Issue Queue"
        subtitle={data ? `${data.issues.length} current signals from flags, workflows, integrations, and delivery logs.` : undefined}
        action={<AlertTriangle size={18} />}
      >
        {isLoading ? (
          <div className="loading-row">Loading issues…</div>
        ) : data?.issues.length ? (
          <div className="stack-list">
            {data.issues.map((issue) => <IssueItem key={issue.id} issue={issue} />)}
          </div>
        ) : (
          <EmptyState title="No current issues" body="Open document flags, stuck workflows, and failed deliveries will appear here." />
        )}
      </SectionCard>
    </div>
  );
}
